import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  axios,
  CompanyBenefit,
  CompanyBenefitService,
  CompanyEmployee,
  CompanyEmployeeService,
  EmployeeTokenService,
} from 'us-web-services';
import { getShortDateString, isBeforeNow, todayYmd } from 'us-react-components/dist/utils/date';

import { Button, ButtonType } from '../../components';
import { Storage } from '../../constants';
import { useDependencies } from '../../dependencies';
import { Routes } from '../../Routes';

export const ActivateBenefits = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { stores } = useDependencies();
  const user = stores.user.user;
  const parentStore = stores.parent;

  const [benefits, setBenefits] = useState<CompanyBenefit | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const token = searchParams.get('token') || undefined;
  const storedCompanyId = typeof window !== 'undefined'
    ? localStorage.getItem(Storage.COMPANY_ID) ?? undefined
    : undefined;
  const storedCompanyEmail = typeof window !== 'undefined'
    ? localStorage.getItem(Storage.COMPANY_EMAIL) ?? undefined
    : undefined;
  const companyValidation = typeof window !== 'undefined'
    ? localStorage.getItem(Storage.COMPANY_VALIDATION) !== 'false'
    : true;

  useEffect(() => {
    document.title = 'UrbanSitter - Activate Benefits';
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    const loadCompanyBenefits = async (companyId: number | string): Promise<void> => {
      try {
        const resp = await CompanyBenefitService.getByFilter({
          params: {
            companyId,
            q: `end.gt(date.valueOf('${todayYmd()}'))`,
            statusId: 2,
          },
        });
        if (cancelled) return;
        const list = (resp.data?.data ?? []) as CompanyBenefit[];
        if (list.length) {
          setBenefits(list[0]);
        } else {
          localStorage.removeItem(Storage.COMPANY_EMPLOYEE);
          toast.error(t('pages.activateBenefits.unableToLocate'));
          setTimeout(() => navigate(Routes.parent.dashboard), 4000);
        }
      } catch (err) {
        console.warn('[ActivateBenefits] benefits load failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const lookupToken = async (): Promise<void> => {
      try {
        const resp = await EmployeeTokenService.getByFilter({
          params: { token, userId: user.id },
        });
        if (cancelled) return;
        const list = resp.data?.data ?? [];
        if (list[0]?.companyId) {
          await loadCompanyBenefits(list[0].companyId);
        } else {
          localStorage.removeItem(Storage.COMPANY_EMPLOYEE);
          toast.error(
            <span>
              {t('pages.activateBenefits.invalidTokenPrefix')}{' '}
              <a
                href='https://benefits.urbansitter.com/enroll'
                className='underline font-semibold'
              >
                {t('pages.activateBenefits.invalidTokenLink')}
              </a>
            </span>,
            { duration: 6000 },
          );
          setTimeout(() => navigate(Routes.parent.dashboard), 6000);
          setLoading(false);
        }
      } catch (err) {
        console.warn('[ActivateBenefits] token lookup failed', err);
        setLoading(false);
      }
    };

    if (token) {
      void lookupToken();
    } else if (storedCompanyId && !companyValidation) {
      void loadCompanyBenefits(storedCompanyId);
    } else {
      navigate(Routes.parent.dashboard, { replace: true });
    }

    return (): void => {
      cancelled = true;
    };
    // storedCompanyId, storedCompanyEmail, companyValidation, token are snapshot
    // reads from URL/localStorage — intentionally not deps to avoid re-firing
    // on storage changes mid-session.

  }, [user?.id]);

  const benefitsBegan = isBeforeNow(benefits?.start);

  const connectBenefits = async (): Promise<void> => {
    if (submitting || !user?.id) return;
    setSubmitting(true);

    const body: CompanyEmployee = { userId: user.id };
    if (!companyValidation && storedCompanyId) {
      body.companyId = Number(storedCompanyId);
      body.email = storedCompanyEmail;
    } else if (token) {
      body.employeeToken = token;
    }

    try {
      await CompanyEmployeeService.create(body);

      const alertMessage = benefitsBegan
        ? t('pages.activateBenefits.toast.activated')
        : t('pages.activateBenefits.toast.confirmedWillBegin', {
            date: getShortDateString(benefits?.start),
          });

      // Refresh the parent to pick up the new companyEmployee relation.
      await parentStore.refreshParent?.();

      localStorage.removeItem(Storage.COMPANY_ID);
      localStorage.removeItem(Storage.COMPANY_EMAIL);
      localStorage.removeItem(Storage.COMPANY_VALIDATION);
      localStorage.removeItem(Storage.COMPANY_EMPLOYEE);

      setTimeout(() => {
        toast.success(alertMessage);
        const nextPath = parentStore.hasValidAddress()
          ? Routes.parent.dashboard
          : Routes.parent.registration.trustSafety;
        navigate(nextPath);
      }, 3000);
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.data?.errors?.[0]?.message) {
        toast.error(e.response.data.errors[0].message);
      } else {
        toast.error(t('pages.activateBenefits.toast.connectFailed'));
      }
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className='container py-8 text-center'>
        <p className='text-neutral-600'>{t('common.loading')}</p>
      </div>
    );
  }

  if (!benefits) {
    return (
      <div className='container py-8 text-center'>
        <p className='text-neutral-600'>{t('pages.activateBenefits.unableToLocate')}</p>
      </div>
    );
  }

  return (
    <div className='container py-12 max-w-xl mx-auto'>
      <div className='us-card rounded p-6'>
        <h1 className='text-2xl font-semibold mb-4'>{t('pages.activateBenefits.heading')}</h1>

        {benefits.company?.logoUrl && (
          <div className='text-center mb-6'>
            <img src={benefits.company.logoUrl} alt={benefits.company.name ?? ''} className='inline-block max-h-24' />
          </div>
        )}

        <h2 className='text-lg font-semibold mb-2'>
          {benefitsBegan
            ? t('pages.activateBenefits.willStartOnceActivated')
            : t('pages.activateBenefits.willStartOn', { date: getShortDateString(benefits.start) })}
        </h2>
        <p className='mb-4'>{benefits.description}</p>

        {!benefitsBegan && (
          <p className='mb-6 text-sm'>
            {t('pages.activateBenefits.exploreUntil', { date: getShortDateString(benefits.start) })}
          </p>
        )}

        <div className='text-center mt-6 space-x-2'>
          <Button
            buttonType={ButtonType.primary}
            onClick={connectBenefits}
            disabled={submitting}
          >
            {benefitsBegan
              ? t('pages.activateBenefits.activateNow')
              : t('pages.activateBenefits.confirmBenefits')}
            {submitting && (
              <span
                className='ml-2 inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin align-middle'
                aria-hidden='true'
              />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
});
